import Time from 'services/base/time';
import { CountryCode } from 'services/base/countries';
import { Optional, OrderedMap, ServiceStatus } from 'services/base/types';

import { VPNServerAppStatusMap } from 'services/accounts/protos/vpnserver_pb';

import { TransportProtocol, ApplicationProtocol } from 'assets/enums/protocols/protocols';

import { HostDeviceTypeMap, HostHistoryDirectionMap } from './protos/hosts_pb';
import { SchedulePolicyCategoryMap, SchedulePolicyKindMap } from './protos/schedules_pb';
import { NetworkHistoryDirectionMap } from './protos/network_pb';

export type NetworkCounters = {
  filtersTotal: number;
  filtersBlocked: number;

  adblockTotal: number;
  adblockBlocked: number;

  maliciousTotal: number;
  maliciousBlocked: number;

  malwareTotal: number;
  malwareBlocked: number;
};

export type IPangolinDomain = {
  id: string;
  hosts: HostMap;
  issues: IssueMap;
  issuesItem: Optional<Issue>;
  issuesOffset: number;
  issuesCapped: boolean;
  issuesFilter: IssuesFilter;
  issuesFiltered: boolean;
  issuesStatus: ServiceStatus;
  history: HistoryMap;
  historyItem: Optional<History>;
  historyOffset: number;
  historyCapped: boolean;
  historyFilter: HistoryFilter;
  historyFiltered: boolean;
  historyStatus: ServiceStatus;
  schedules: ScheduleMap;
  updateReleases: UpdateReleaseMap;
  updateSnapshots: UpdateSnapshotMap;
  updateUpgrades: UpdateUpgradeMap;
  updateRestores: UpdateRestoreMap;
  updateHistory: UpdateHistoryRefMap;
  vpnServer: VPNServer;
  vpnClients: VPNClientMap;
  throughput: Record<NetworkThroughputDuration, CachedNetworkThroughput>;
  throughputEnabled: ServiceStatus;
  counters: Optional<NetworkCounters>;
};

export type IPangolinDomains = {
  domains: OrderedMap<string, IPangolinDomain>;
};

export type DeviceType = HostDeviceTypeMap[keyof HostDeviceTypeMap];
export type SchedulePolicyKind = SchedulePolicyKindMap[keyof SchedulePolicyKindMap];
export type SchedulePolicyCategory = SchedulePolicyCategoryMap[keyof SchedulePolicyCategoryMap];
export type HistoryDirection = NetworkHistoryDirectionMap[keyof NetworkHistoryDirectionMap] | HostHistoryDirectionMap[keyof HostHistoryDirectionMap];

export type Host = {
  id: string;
  mac: string;
  ip: string;
  gateway: boolean;
  name: string;
  machineOS: string;
  humanReadableOS: string;
  deviceType: DeviceType;
  lastSeen: Optional<Date>;
  uploadAllocation: number;
  downloadAllocation: number;
  customName: Optional<string>;
  customDeviceType: Optional<DeviceType>;
  customOS: Optional<string>;
  blockInternet: Optional<boolean>;
  blockInternal: Optional<boolean>;
  blockAdTracking: Optional<boolean>;
  blockMalicious: Optional<boolean>;
  ignoreSchedules: Optional<boolean>;
  ignoreAll: Optional<boolean>;
  notifyOnUp: Optional<boolean>;
  masquerade: Optional<boolean>;
  isOnline: boolean;
  throughput: Record<NetworkThroughputDuration, CachedHostThroughput>;
};

export enum IssueKind {
  MALICIOUS_DOMAIN,
  MALICIOUS_ADDRESS,
  MALWARE_DOMAIN,
  MALWARE_ADDRESS,
  UNKNOWN,
}

export enum IssueSeverity {
  VERY_LOW,
  LOW,
  MEDIUM,
  HIGH,
  CRITICAL,
}

export type MaliciousDomainIssue = {
  kind: IssueKind.MALICIOUS_DOMAIN;
  data: {
    mac: string;
    domain: string;
  };
};

export type MaliciousAddressIssue = {
  kind: IssueKind.MALICIOUS_ADDRESS;
  data: {
    mac: string;
    address: string;
  };
};

export type MalwareDomainIssue = {
  kind: IssueKind.MALWARE_DOMAIN;
  data: {
    mac: string;
    domain: string;
    families: string[];
    revision: string[];
  };
};

export type MalwareAddressIssue = {
  kind: IssueKind.MALWARE_ADDRESS;
  data: {
    mac: string;
    address: string;
    families: string[];
    revision: string[];
  };
};

export type UnknownIssue = {
  kind: IssueKind.UNKNOWN;
} & CommonIssueMembers;

type CommonIssueMembers = {
  fpid: string;
  severity: IssueSeverity;
  action: string;
  incidents: IncidentMap;
};

export type KnownIssue = CommonIssueMembers & (
  | MaliciousDomainIssue
  | MaliciousAddressIssue
  | MalwareDomainIssue
  | MalwareAddressIssue
);

export type Issue = KnownIssue | UnknownIssue;

export type IssuesFilter = {
  search: string;
  start: Date;
  end: Date;
  seen: IssuesFilterSeen;
  severityMin: number;
  severityMax: number;
  affectedMin: number;
  affectedMax: number;
  incidentsMin: number;
  incidentsMax: number;
  kinds: IssueKind[];
  devices: string[];
};

export type IssuesFilterSeen = {
  seen: boolean;
  unseen: boolean;
};

export type Incident = {
  uuid: string;
  timestamp: Date;
  seen: boolean;
};

export type Schedule = {
  uuid: string;
  name: string;
  enabled: boolean;
  start: Time;
  end: Time;
  days: ScheduleDays;
  applyToAllHosts: boolean;
  applyAllPolicies: boolean;
  hostIDs: string[];
  policies: SchedulePolicyMap;
};

export type ScheduleDays = {
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
};

export type UnknownPolicy = {
  kind: SchedulePolicyKindMap['P_UNKNOWN'];
  uuid: string;
};
export type CategoryPolicy = {
  kind: SchedulePolicyKindMap['P_CATEGORY'];
  uuid: string;
  category: SchedulePolicyCategory;
};
export type CustomPolicy = {
  kind: SchedulePolicyKindMap['P_CUSTOM'];
  uuid: string;
  custom: string;
};
export type SchedulePolicy =
  | UnknownPolicy
  | CategoryPolicy
  | CustomPolicy;

export type History = {
  order: number;
  ctid: number;
  start: number;
  last: number;
  direction: HistoryDirection;
  mac_src: string;
  mac_dst: string;
  proto_l3: number;
  addr_src: string;
  addr_dst: string;
  proto_l4: number;
  port_src: number;
  port_dst: number;
  pkts_orig: number;
  pkts_repl: number;
  bytes_orig: number;
  bytes_repl: number;
  allowed: boolean;
  domains_src: Optional<string[]>;
  domains_dst: Optional<string[]>;
  location_src: Optional<HistoryLocation>;
  location_dst: Optional<HistoryLocation>;
};

export type HistoryIndex = {
  order: number;
  ctid: number;
  start: number;
};

export type HistoryLocation = {
  country_short: string;
  country_long: string;
  region: string;
  city: string;
  zipcode: string;
  timezone: string;
  ip: string;
  longitude: number;
  latitude: number;
};

export type HistoryFilter = {
  start: Date;
  end: Date;
  directions: HistoryFilterDirections;
  actions: HistoryFilterActions;
  protocols: ProtocolFilter[];
  sites: SiteFilter[];
  devices: string[];
  locations: CountryCode[];
};

export type HistoryFilterDirections = {
  outbound: boolean;
  inbound: boolean;
  local: boolean;
};

export type HistoryFilterActions = {
  allowed: boolean;
  blocked: boolean;
};

export enum ProtocolFilterType {
  APPLICATION,
  TRANSPORT_PORT,
  TRANSPORT_RANGE,
}

export type ApplicationProtocolFilterDetails = {
  filters: {
    application: ApplicationProtocolFilter;
    transport: TransportProtocolFilter[];
  };
};

export const APPLICATION_PROTOCOL_FILTERS = new Map<ApplicationProtocol, ApplicationProtocolFilterDetails>([
  [ApplicationProtocol.FTP, {
    filters: {
      application: {
        type: ProtocolFilterType.APPLICATION,
        uuid: '93f6b28a-79ca-4d48-bef3-d6ef7704e2a7',
        application: ApplicationProtocol.FTP,
      },
      transport: [{
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: '24559b25-3b12-4293-a1c2-eb21790259b8',
        transport: TransportProtocol.TCP,
        port: 20,
      }, {
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: 'b6bc9e06-0bb6-4cc7-a2e1-bbee467bf668',
        transport: TransportProtocol.TCP,
        port: 21,
      }],
    },
  }],
  [ApplicationProtocol.SSH, {
    filters: {
      application: {
        type: ProtocolFilterType.APPLICATION,
        uuid: '58f1501f-2887-4ddc-9233-992fcfd32630',
        application: ApplicationProtocol.SSH,
      },
      transport: [{
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: '30c40072-76c1-4f54-a326-eeaecd86a569',
        transport: TransportProtocol.TCP,
        port: 22,
      }],
    },
  }],
  [ApplicationProtocol.TELNET, {
    filters: {
      application: {
        type: ProtocolFilterType.APPLICATION,
        uuid: '21cd1647-3dcd-4f1e-b8e6-ddfafd878f67',
        application: ApplicationProtocol.TELNET,
      },
      transport: [{
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: 'ac1e905f-77cd-4e7c-9034-3d82927d9406',
        transport: TransportProtocol.TCP,
        port: 23,
      }],
    },
  }],
  [ApplicationProtocol.SMTP, {
    filters: {
      application: {
        type: ProtocolFilterType.APPLICATION,
        uuid: 'bf8eb355-d8bf-4dfd-8272-74720be2e483',
        application: ApplicationProtocol.SMTP,
      },
      transport: [{
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: '7115425a-7a86-4038-80f3-7a3aa0634925',
        transport: TransportProtocol.TCP,
        port: 25,
      }],
    },
  }],
  [ApplicationProtocol.DNS, {
    filters: {
      application: {
        type: ProtocolFilterType.APPLICATION,
        uuid: '8be92102-7eeb-4bc8-8a9b-cba4cb7d3339',
        application: ApplicationProtocol.DNS,
      },
      transport: [{
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: '6f404f08-c141-472d-8484-bd1ad0ef31eb',
        transport: TransportProtocol.UDP,
        port: 53,
      }],
    },
  }],
  [ApplicationProtocol.DHCP, {
    filters: {
      application: {
        type: ProtocolFilterType.APPLICATION,
        uuid: 'b3e04cdc-1f1d-429e-b62e-0650c55ef930',
        application: ApplicationProtocol.DHCP,
      },
      transport: [{
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: '48eddc9f-e3be-4928-bd02-93e623b57e8c',
        transport: TransportProtocol.UDP,
        port: 67,
      }, {
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: '47b64107-a7cb-4967-b394-c21ee71b6654',
        transport: TransportProtocol.UDP,
        port: 68,
      }],
    },
  }],
  [ApplicationProtocol.TFTP, {
    filters: {
      application: {
        type: ProtocolFilterType.APPLICATION,
        uuid: 'ae535a8c-e64b-44a8-8f53-c307034d32c5',
        application: ApplicationProtocol.TFTP,
      },
      transport: [{
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: 'ff39de61-ee7e-4447-bf15-50d65875171e',
        transport: TransportProtocol.UDP,
        port: 69,
      }],
    },
  }],
  [ApplicationProtocol.HTTP, {
    filters: {
      application: {
        type: ProtocolFilterType.APPLICATION,
        uuid: '164d6673-a3e2-4e40-b4a7-503b870330c9',
        application: ApplicationProtocol.HTTP,
      },
      transport: [{
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: 'b2a50cdd-888e-4837-844b-f5270c480d04',
        transport: TransportProtocol.TCP,
        port: 80,
      }, {
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: 'de3df257-b96e-434a-8a80-fc7f855eb1d9',
        transport: TransportProtocol.UDP,
        port: 80,
      }],
    },
  }],
  [ApplicationProtocol.POP2, {
    filters: {
      application: {
        type: ProtocolFilterType.APPLICATION,
        uuid: '0e48fc28-f75b-42dc-901f-1feb6c23437c',
        application: ApplicationProtocol.POP2,
      },
      transport: [{
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: 'd2c90c51-1c61-41ff-b683-7b9594a7b330',
        transport: TransportProtocol.TCP,
        port: 109,
      }],
    },
  }],
  [ApplicationProtocol.POP3, {
    filters: {
      application: {
        type: ProtocolFilterType.APPLICATION,
        uuid: '4d535f44-99ad-4a96-9d39-b295d9536308',
        application: ApplicationProtocol.POP3,
      },
      transport: [{
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: '3c454cb7-8489-4378-8f46-88243363cdc2',
        transport: TransportProtocol.TCP,
        port: 110,
      }],
    },
  }],
  [ApplicationProtocol.NTP, {
    filters: {
      application: {
        type: ProtocolFilterType.APPLICATION,
        uuid: '0d7b5107-8e25-4e3a-b6f8-af3ce70b40eb',
        application: ApplicationProtocol.NTP,
      },
      transport: [{
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: '93474fef-3c33-41cf-aa6d-bd7895c16274',
        transport: TransportProtocol.UDP,
        port: 123,
      }],
    },
  }],
  [ApplicationProtocol.NETBIOS, {
    filters: {
      application: {
        type: ProtocolFilterType.APPLICATION,
        uuid: 'c37e4c77-9112-49f6-9c34-fd576dfcc913',
        application: ApplicationProtocol.NETBIOS,
      },
      transport: [{
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: 'e658d7a0-c845-4645-aa92-0520276f8fbb',
        transport: TransportProtocol.TCP,
        port: 137,
      }, {
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: 'd6e249c7-e7ea-46f5-97cf-fe2d1617cae5',
        transport: TransportProtocol.UDP,
        port: 137,
      }, {
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: '40fda985-f93e-4b2e-a661-ca6758aa0ff9',
        transport: TransportProtocol.UDP,
        port: 138,
      }, {
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: '8525b034-176d-46aa-b6b7-67b80f7176a8',
        transport: TransportProtocol.TCP,
        port: 139,
      }],
    },
  }],
  [ApplicationProtocol.IMAP, {
    filters: {
      application: {
        type: ProtocolFilterType.APPLICATION,
        uuid: '4673b5aa-4a5f-442d-8ab1-88bb2f0f5d72',
        application: ApplicationProtocol.IMAP,
      },
      transport: [{
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: 'dcc59868-1a99-4038-a751-e235755502fd',
        transport: TransportProtocol.TCP,
        port: 143,
      }],
    },
  }],
  [ApplicationProtocol.HTTPS, {
    filters: {
      application: {
        type: ProtocolFilterType.APPLICATION,
        uuid: 'b7eb3cb8-e254-4d27-acd3-ac19fbd1b055',
        application: ApplicationProtocol.HTTPS,
      },
      transport: [{
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: '7c4c39a3-86d3-4c8b-8f73-6fa4f2047408',
        transport: TransportProtocol.TCP,
        port: 443,
      }, {
        type: ProtocolFilterType.TRANSPORT_PORT,
        uuid: '278b5de6-d178-4baa-bde7-2b787c6feada',
        transport: TransportProtocol.UDP,
        port: 443,
      }],
    },
  }],
]);

export type ApplicationProtocolFilter = {
  type: ProtocolFilterType.APPLICATION;
  uuid: string;
  application: ApplicationProtocol;
};

export type PortTransportProtocolFilter = {
  type: ProtocolFilterType.TRANSPORT_PORT;
  uuid: string;
  transport: Optional<TransportProtocol>;
  port: number;
};

export type RangeTransportProtocolFilter = {
  type: ProtocolFilterType.TRANSPORT_RANGE;
  uuid: string;
  transport: Optional<TransportProtocol>;
  port_first: number;
  port_final: number;
};

export type TransportProtocolFilter = PortTransportProtocolFilter | RangeTransportProtocolFilter;
export type ProtocolFilter = ApplicationProtocolFilter | TransportProtocolFilter;

export enum SiteFilterType {
  DOMAIN,
  ADDRESS,
  RANGE,
  NETWORK,
}

export type DomainSiteFilter = {
  type: SiteFilterType.DOMAIN;
  uuid: string;
  domain: string;
};

export type AddressSiteFilter = {
  type: SiteFilterType.ADDRESS;
  uuid: string;
  address: string;
};

export type RangeSiteFilter = {
  type: SiteFilterType.RANGE;
  uuid: string;
  first: string;
  final: string;
};

export type NetworkSiteFilter = {
  type: SiteFilterType.NETWORK;
  uuid: string;
  address: string;
  mask: number;
};

export type SiteFilter =
  | DomainSiteFilter
  | AddressSiteFilter
  | RangeSiteFilter
  | NetworkSiteFilter;

export type NetworkThroughputData = {
  start: Date;
  end: Date;

  egressPackets: number;
  egressBytes: number;

  ingressPackets: number;
  ingressBytes: number;

  internalPackets: number;
  internalBytes: number;

  allPackets: number;
  allBytes: number;
};

export type HostThroughputData = {
  start: Date;
  end: Date;

  externalTxPackets: number;
  externalTxBytes: number;
  externalRxPackets: number;
  externalRxBytes: number;

  internalTxPackets: number;
  internalTxBytes: number;
  internalRxPackets: number;
  internalRxBytes: number;

  allTxPackets: number;
  allTxBytes: number;
  allRxPackets: number;
  allRxBytes: number;
};

export enum NetworkThroughputDuration {
  DAILY = '1d',
  WEEKLY = '1w',
  HOURLY = '1h',
}

export type CachedNetworkThroughput = {
  status: Exclude<ServiceStatus, ServiceStatus.LOADED>;
} | {
  status: ServiceStatus.LOADED;
  data: NetworkThroughputData[];
};

export type CachedHostThroughput = {
  status: Exclude<ServiceStatus, ServiceStatus.LOADED>;
} | {
  status: ServiceStatus.LOADED;
  data: HostThroughputData[];
};

export enum UpdateUpgradeStatus {
  PENDING = 'pending',
  DOWNLOAD = 'download',
  INSTALL = 'install',
  REBOOT = 'reboot',
  LAUNCH = 'launch',
  BACKUP = 'backup',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  SKIPPED = 'skipped',
  FAILED = 'failed',
}

export enum UpdateRestoreStatus {
  PENDING = 'pending',
  ROLLBACK = 'rollback',
  DISPATCH = 'dispatch',
  DOWNLOAD = 'download',
  INSTALL = 'install',
  REBOOT = 'reboot',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  SKIPPED = 'skipped',
  FAILED = 'failed',
}

export enum UpdateHistoryType {
  UPGRADE = 'upgrade',
  RESTORE = 'restore',
}

export type UpdateRelease = {
  id: string;
  tag: string;
  notes: Optional<string>;
  is_enabled: boolean;
  created_at: Date;
  updated_at: Date;
};

export type UpdateSnapshot = {
  id: string;
  release_id: string;
  is_enabled: boolean;
  created_at: Date;
  updated_at: Date;
  size?: number;
};

export type UpdateUpgrade = {
  id: string;
  source_release_id: Optional<string>;
  target_release_id: string;
  status: UpdateUpgradeStatus;
  created_at: Date;
  updated_at: Date;
  terminal_status: Optional<UpdateUpgradeStatus>;
};

export type UpdateRestore = {
  id: string;
  source_release_id: Optional<string>;
  target_release_id: string;
  target_snapshot_id: string;
  status: UpdateRestoreStatus;
  created_at: Date;
  updated_at: Date;
  terminal_status: Optional<UpdateRestoreStatus>;
};

export type UpdateHistory =
  | { type: UpdateHistoryType.UPGRADE, upgrade: UpdateUpgrade }
  | { type: UpdateHistoryType.RESTORE, restore: UpdateRestore };

export type UpdateHistoryRef =
  | { type: UpdateHistoryType.UPGRADE, upgrade_id: string }
  | { type: UpdateHistoryType.RESTORE, restore_id: string };

export type VPNClientStatus = VPNServerAppStatusMap[keyof VPNServerAppStatusMap];

export type VPNClient = {
  id: string;
  name: string;
  nonce: string;
  mnemonic: string;
  status: VPNClientStatus;
  isEnabled: boolean;
  publicIP: Optional<string>;
  lastSeen: Optional<Date>;
  hostId: Optional<string>;
};

export type VPNServer = {
  isEnabled: boolean;
};

export class HostMap extends OrderedMap<string, Host> {

  getMac(mac: string) {
    return this.get(mac.toLowerCase().replace(/:/g, ''));
  }

}

export class IssueMap extends OrderedMap<string, Issue> {}
export class IncidentMap extends OrderedMap<string, Incident> {}

export class HistoryMap extends OrderedMap<string, History> {}

export class ScheduleMap extends OrderedMap<string, Schedule> {}
export class SchedulePolicyMap extends OrderedMap<string, SchedulePolicy> {}

export class UpdateReleaseMap extends OrderedMap<string, UpdateRelease> {}
export class UpdateSnapshotMap extends OrderedMap<string, UpdateSnapshot> {}
export class UpdateUpgradeMap extends OrderedMap<string, UpdateUpgrade> {}
export class UpdateRestoreMap extends OrderedMap<string, UpdateRestore> {}
export class UpdateHistoryMap extends OrderedMap<string, UpdateHistory> {}
export class UpdateHistoryRefMap extends OrderedMap<string, UpdateHistoryRef> {}

export class VPNClientMap extends OrderedMap<string, VPNClient> {}
